import { NumberInput } from '@/components/blocks/NumberInput';
import { useStoreLocale } from '@/data/Content/StoreLocale';
import { useLocalization } from '@/data/Localization';
import { useSettings } from '@/data/Settings';
import { useUser } from '@/data/User';
import { getCurrencySymbol } from '@/utils/formatPrice';
import { getCurrencyFromContext } from '@/utils/getCurrencyFromContext';
import { Box, Button, Stack, Typography } from '@mui/material';
import { Dispatch, FC, SetStateAction, useCallback, useMemo } from 'react';

const isPriceRangeValid = (minPrice: number | null, maxPrice: number | null): boolean => {
	if (minPrice == null || maxPrice == null) return true;

	return minPrice <= maxPrice;
};
const MINS = {
	min: 0,
	max: 1,
};

type Props = {
	isMobile?: boolean;
	btnDisabled?: boolean;
	minPrice?: number | null;
	maxPrice?: number | null;
	setMaxPrice?: Dispatch<SetStateAction<number | null>>;
	setMinPrice?: Dispatch<SetStateAction<number | null>>;
	onSubmitPrice?: () => void;
};

export const FacetNavigationPriceRangePicker: FC<Props> = ({
	isMobile,
	btnDisabled,
	minPrice = null,
	maxPrice = null,
	setMaxPrice,
	setMinPrice,
	onSubmitPrice,
}) => {
	const { localeName: locale } = useStoreLocale();
	const { settings } = useSettings();
	const { defaultCurrency } = settings;

	const productFilterNLS = useLocalization('ProductFilter');
	const { user } = useUser();
	const currency = useMemo(
		() => getCurrencyFromContext(user?.context) ?? defaultCurrency,
		[user, defaultCurrency]
	);
	
	const currencySymbol = useMemo(() => getCurrencySymbol(locale, currency), [locale, currency]);

	const onChange = useCallback((label: 'min' | 'max', value: number | null) => {
		const val = value === null ? null : value < MINS[label] ? MINS[label] : value;

		if (label === 'min') {
			typeof setMinPrice === 'function' && setMinPrice(val);
		} else {
			typeof setMaxPrice === 'function' && setMaxPrice(val);
		}
	}, []);

	const error = useMemo(() => !isPriceRangeValid(minPrice, maxPrice), [maxPrice, minPrice]);

	return (
		<Stack spacing={1}>
			<Stack spacing={1} direction="row">
				<NumberInput
					value={minPrice}
					min={MINS.min}
					precision={2}
					placeholder={productFilterNLS.Labels.minPrice.t()}
					onChange={(num) => onChange('min', num)}
					error={error}
					maxLength={12}
					data-testid="productFilter-min-price-range-picker-quantity"
					id="productFilter-min-price-range-picker-quantity"
					className="price-input-min"
					altControls
				/>
				<Box display={'flex'} justifyContent={'center'} alignItems={'center'} width={'5px'}>
					<Typography>{productFilterNLS.Labels.To.t()}</Typography>
				</Box>
				<NumberInput
					value={maxPrice}
					min={MINS.max}
					precision={2}
					placeholder={productFilterNLS.Labels.maxPrice.t()}
					onChange={(num) => onChange('max', num)}
					error={error}
					maxLength={12}
					data-testid="productFilter-max-price-range-picker-quantity"
					id="productFilter-max-price-range-picker-quantity"
					className="price-input-max"
					altControls
				/>
			</Stack>
			{!isMobile && (
				<Box>
					<Button
						data-testid="productFilter_price_facet"
						id="applyPriceFilter"
						disabled={btnDisabled}
						size="small"
						fullWidth
						variant="outlined"
						onClick={onSubmitPrice}
					>
						{productFilterNLS.Actions.Filter.t()}
					</Button>
				</Box>
			)}
		</Stack>
	);
};
